import "whatwg-fetch";

export const errors = {
    ignoreErrors: "ignore errors",
    unauthorized: "Oops, we haven't got authorization to access!",
    forbidden: "Oops, we haven't got permission to access!",
};

type HttpHeaders = Dictionary<string>;
type UploadFileOptions = {
    endpoint: string;
    file: File;
    headers?: HttpHeaders;
    dataFormatter?: F1<Dictionary<any>, Dictionary<any>>;
    chunkSize?: number;
};

const handleErrors = (response: Response): Response => {
    if (response.status === 401) {
        setTimeout(() => location.reload(), 0);
        throw new Error(errors.unauthorized);
    }

    if (response.status === 403) {
        ep.errorManager.handleUnknownError();
        throw new Error(errors.forbidden);
    }

    if (!response.ok) {
        throw new Error(errors.ignoreErrors);
    }

    return response;
};

class UploadFile {
    file: File;
    headers: HttpHeaders;
    endpoint: string;

    chunkSize = 50 * 1024 * 1024; // 50 Mb
    chunk: Blob;
    chunkCount: number;
    totalChunks: number;
    dataFormatter: F1<Dictionary<any>, Dictionary<any>>;

    constructor(options: UploadFileOptions) {
        this.endpoint = options.endpoint;
        this.file = options.file;
        this.headers = options.headers || {};
        this.dataFormatter = options.dataFormatter;
        this.chunkSize = options.chunkSize ?? this.chunkSize;

        this.chunkCount = 0;
        this.totalChunks = Math.ceil(this.file.size / this.chunkSize);
    }

    sendChunks(body?: Dictionary<any>): Promise<any> {
        return this.sendChunk(body).then(data => {
            this.chunkCount = this.chunkCount + 1;

            if (data && this.chunkCount < this.totalChunks) {
                const body = this.dataFormatter ? this.dataFormatter(data) : null;

                return this.sendChunks(body);
            }

            return data || {};
        });
    }

    sendChunk(body: Dictionary<any>) {
        const length = this.totalChunks === 1 ? this.file.size : this.chunkSize;
        const rangeStart = length * this.chunkCount;

        this.chunk = this.file.slice(rangeStart, rangeStart + length, this.file.type);

        const rangeEnd = rangeStart + this.chunk.size - 1;

        const formData = new FormData();
        formData.append("file", this.chunk, this.file.name);

        if (typeof body === "object" && body !== null) {
            Object.keys(body).map(key => {
                formData.append(key, body[key]);
            });
        }

        const headers = new Headers({
            ...this.headers,
            "Content-Range": `bytes ${rangeStart}-${rangeEnd}/${this.file.size}`,
            "Content-Disposition": `attachment; filename="${encodeURI(this.file.name)}"`,
        });

        return new Promise((resolve, reject) => {
            fetch(this.endpoint, {
                method: "POST",
                body: formData,
                headers,
            })
                .then(handleErrors)
                .then(response => {
                    const contentType = response.headers.get("content-type");

                    if (!contentType || !contentType.includes("application/json")) {
                        resolve(response);
                    }

                    resolve(response.json());
                })
                .catch((error: Error) => {
                    const { message } = error;

                    if (message === errors.unauthorized || message === errors.forbidden) {
                        reject(error);
                    } else {
                        resolve(null);
                    }
                });
        });
    }
}

export const createUpload = (options: UploadFileOptions) => {
    const upload = new UploadFile(options);

    return upload.sendChunks();
};
